"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

export type CartItem = {
  itemSlug: string;
  itemName: string;
  size: string | null;
  color: string | null;
  quantity: number;
  imageRef?: string | null;
  approvalRequired?: boolean;
  onePerEmployee?: boolean;
  onePerEmployeeOverrideReason?: string;
};

export function cartItemKey(item: Pick<CartItem, "itemSlug" | "size" | "color">) {
  return `${item.itemSlug}::${item.size ?? ""}::${item.color ?? ""}`;
}

type CartContextType = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (key: string) => void;
  updateQuantity: (key: string, quantity: number) => void;
  clearCart: () => void;
  count: number;
};

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("merch-cart");
      if (stored) setItems(JSON.parse(stored));
    } catch {
      // ignore invalid stored JSON
    }

    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) localStorage.setItem("merch-cart", JSON.stringify(items));
  }, [items, hydrated]);

  function addItem(item: CartItem) {
    const key = cartItemKey(item);
    setItems((prev) => {
      const existing = prev.find((i) => cartItemKey(i) === key);
      if (existing) {
        return prev.map((i) =>
          cartItemKey(i) === key ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }

      return [...prev, item];
    });
  }

  function removeItem(key: string) {
    setItems((prev) => prev.filter((i) => cartItemKey(i) !== key));
  }

  function updateQuantity(key: string, quantity: number) {
    if (quantity < 1) {
      removeItem(key);
      return;
    }

    setItems((prev) => prev.map((i) => (cartItemKey(i) === key ? { ...i, quantity } : i)));
  }

  function clearCart() {
    setItems([]);
    localStorage.removeItem("merch-cart");
  }

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        count: items.reduce((sum, i) => sum + i.quantity, 0),
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
